import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  IconButton,
  AppBar,
  Toolbar,
  Drawer,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Restaurant as RestaurantIcon,
  MenuBook,
  LocationOn,
  Phone,
  Email,
  Language,
  ArrowBack,
  Info as InfoIcon,
  Menu as MenuIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { restaurantApi, handleApiError } from '../services/api';
import { applyCuisineColors, resetToDefaultTheme } from '../utils/restaurantColors';
import ChatBot from '../components/ChatBot';
import type { Restaurant } from '../types';

const DRAWER_WIDTH = 320;

const RestaurantDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [selectedSection, setSelectedSection] = useState<'info' | 'menu'>('info');
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);

  useEffect(() => {
    if (id) {
      loadRestaurant(id);
    }
  }, [id]);

  useEffect(() => {
    setDrawerOpen(!isMobile);
  }, [isMobile]);

  // Apply cuisine-specific colors when restaurant loads
  useEffect(() => {
    if (restaurant?.cuisine_type) {
      applyCuisineColors(restaurant.cuisine_type);
    }
    
    // Cleanup when component unmounts
    return () => {
      resetToDefaultTheme();
    };
  }, [restaurant?.cuisine_type]);

  const loadRestaurant = async (restaurantId: string) => {
    try {
      setLoading(true);
      const data = await restaurantApi.getRestaurant(restaurantId);
      setRestaurant(data);
      setError('');
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        flexDirection="column"
        sx={{ bgcolor: 'var(--color-background-default)' }}
      >
        <CircularProgress size={60} sx={{ color: 'var(--color-primary-500)' }} />
        <Typography variant="h6" sx={{ mt: 2, color: 'var(--color-text-primary)' }}>
          Loading restaurant...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, bgcolor: 'var(--color-background-default)' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          variant="contained"
          sx={{ 
            bgcolor: 'var(--color-primary-500)',
            '&:hover': { bgcolor: 'var(--color-primary-600)' }
          }}
        >
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  if (!restaurant) {
    return (
      <Box sx={{ p: 3, bgcolor: 'var(--color-background-default)' }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Restaurant not found
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          variant="contained"
          sx={{ 
            bgcolor: 'var(--color-primary-500)',
            '&:hover': { bgcolor: 'var(--color-primary-600)' }
          }}
        >
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'var(--color-background-drawer)' }}>
      {/* Header */}
      <AppBar position="static" elevation={1} sx={{ bgcolor: 'var(--color-background-paper)' }}>
        <Toolbar>
          {isMobile && (
            <IconButton
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 1, color: 'var(--color-text-secondary)' }}
            >
              <CloseIcon />
            </IconButton>
          )}
          <RestaurantIcon sx={{ mr: 1, color: 'var(--color-primary-500)' }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" noWrap sx={{ color: 'var(--color-text-primary)' }}>
              {restaurant.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)' }}>
              {restaurant.cuisine_type}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Navigation */}
      <Box sx={{ p: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          fullWidth
          sx={{ 
            mb: 2,
            borderColor: 'var(--color-primary-300)',
            color: 'var(--color-primary-600)',
            '&:hover': { 
              borderColor: 'var(--color-primary-500)',
              bgcolor: 'var(--color-primary-50)'
            }
          }}
        >
          Back to Dashboard
        </Button>

        <List sx={{ mb: 2 }}>
          <ListItem disablePadding>
            <ListItemButton
              selected={selectedSection === 'info'}
              onClick={() => setSelectedSection('info')}
              sx={{
                '&.Mui-selected': {
                  bgcolor: 'var(--color-primary-50)',
                  color: 'var(--color-primary-700)',
                  '&:hover': { bgcolor: 'var(--color-primary-100)' }
                }
              }}
            >
              <ListItemIcon>
                <InfoIcon sx={{ color: selectedSection === 'info' ? 'var(--color-primary-600)' : 'var(--color-text-secondary)' }} />
              </ListItemIcon>
              <ListItemText primary="Restaurant Information" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              selected={selectedSection === 'menu'}
              onClick={() => setSelectedSection('menu')}
              sx={{
                '&.Mui-selected': {
                  bgcolor: 'var(--color-primary-50)',
                  color: 'var(--color-primary-700)',
                  '&:hover': { bgcolor: 'var(--color-primary-100)' }
                }
              }}
            >
              <ListItemIcon>
                <MenuIcon sx={{ color: selectedSection === 'menu' ? 'var(--color-primary-600)' : 'var(--color-text-secondary)' }} />
              </ListItemIcon>
              <ListItemText primary="Menu Information" />
            </ListItemButton>
          </ListItem>
        </List>

        <Button
          variant="contained"
          startIcon={<MenuBook />}
          onClick={() => navigate(`/restaurant/${restaurant.restaurant_id}/menu`)}
          fullWidth
          sx={{ 
            mb: 2,
            bgcolor: 'var(--color-primary-500)',
            '&:hover': { bgcolor: 'var(--color-primary-600)' }
          }}
        >
          View Menu
        </Button>
      </Box>

      <Divider sx={{ borderColor: 'var(--color-neutral-200)' }} />

      {/* Content Section */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
        {selectedSection === 'info' && (
          <Stack spacing={2}>
            {restaurant.description && (
              <Paper sx={{ p: 2, bgcolor: 'var(--color-background-card)' }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'var(--color-text-primary)' }}>
                  About
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)' }}>
                  {restaurant.description}
                </Typography>
              </Paper>
            )}

            <Paper sx={{ p: 2, bgcolor: 'var(--color-background-card)' }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'var(--color-text-primary)' }}>
                Contact Information
              </Typography>
              <Stack spacing={1}>
                {restaurant.location?.address && (
                  <Box display="flex" alignItems="center">
                    <LocationOn sx={{ mr: 1, fontSize: 20, color: 'var(--color-text-secondary)' }} />
                    <Typography variant="body2" sx={{ color: 'var(--color-text-primary)' }}>
                      {restaurant.location.address}
                      {restaurant.location.city && `, ${restaurant.location.city}`}
                      {restaurant.location.state && `, ${restaurant.location.state}`}
                      {restaurant.location.zip_code && ` ${restaurant.location.zip_code}`}
                    </Typography>
                  </Box>
                )}

                {restaurant.contact?.phone && (
                  <Box display="flex" alignItems="center">
                    <Phone sx={{ mr: 1, fontSize: 20, color: 'var(--color-text-secondary)' }} />
                    <Typography variant="body2" sx={{ color: 'var(--color-text-primary)' }}>
                      {restaurant.contact.phone}
                    </Typography>
                  </Box>
                )}

                {restaurant.contact?.email && (
                  <Box display="flex" alignItems="center">
                    <Email sx={{ mr: 1, fontSize: 20, color: 'var(--color-text-secondary)' }} />
                    <Typography variant="body2" sx={{ color: 'var(--color-text-primary)' }}>
                      {restaurant.contact.email}
                    </Typography>
                  </Box>
                )}

                {restaurant.contact?.website && (
                  <Box display="flex" alignItems="center">
                    <Language sx={{ mr: 1, fontSize: 20, color: 'var(--color-text-secondary)' }} />
                    <Typography variant="body2" sx={{ color: 'var(--color-text-primary)' }}>
                      <a 
                        href={restaurant.contact.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: 'var(--color-primary-600)' }}
                      >
                        {restaurant.contact.website}
                      </a>
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>

            <Paper sx={{ p: 2, bgcolor: 'var(--color-background-card)' }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'var(--color-text-primary)' }}>
                Details
              </Typography>
              <Stack spacing={1}>
                <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)' }}>
                  Created: {formatDate(restaurant.created_at)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)' }}>
                  Updated: {formatDate(restaurant.updated_at)}
                </Typography>
              </Stack>
            </Paper>
          </Stack>
        )}

        {selectedSection === 'menu' && (
          <Paper sx={{ p: 2, bgcolor: 'var(--color-background-card)' }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'var(--color-text-primary)' }}>
              Menu Statistics
            </Typography>
            <Stack spacing={2}>
              {restaurant.metadata?.total_menu_items ? (
                <Typography variant="body2" sx={{ color: 'var(--color-text-primary)' }}>
                  <strong>{restaurant.metadata.total_menu_items}</strong> menu items
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)' }}>
                  No menu items yet
                </Typography>
              )}

              {restaurant.metadata?.menu_last_updated && (
                <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)' }}>
                  Last updated: {formatDate(restaurant.metadata.menu_last_updated)}
                </Typography>
              )}
            </Stack>
          </Paper>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden', bgcolor: 'var(--color-background-default)' }}>
      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={drawerOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Desktop Drawer */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Main Content - Chat Area */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Mobile Header */}
        {isMobile && (
          <AppBar position="static" elevation={1} sx={{ bgcolor: 'var(--color-background-paper)' }}>
            <Toolbar>
              <IconButton
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 1, color: 'var(--color-text-secondary)' }}
              >
                <MenuIcon />
              </IconButton>
              <Typography variant="h6" sx={{ flexGrow: 1, color: 'var(--color-text-primary)' }}>
                {restaurant.name} - Chat
              </Typography>
            </Toolbar>
          </AppBar>
        )}

        {/* Chat Interface */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <ChatBot
            restaurant={restaurant}
            isOpen={true}
            onToggle={() => {}} // No-op since chat is always open in this layout
            fullScreen={true}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default RestaurantDetails; 